import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { Bell, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { Notification } from "@shared/schema";

interface Props {
  inboxHref: string;
  preferencesHref: string;
}

export function NotificationBell({ inboxHref, preferencesHref }: Props) {
  const countQuery = useQuery<{ count: number }>({
    queryKey: ["/api/notifications/unread-count"],
    refetchInterval: 30_000,
    staleTime: 0,
  });

  const listQuery = useQuery<{ items: Notification[] }>({
    queryKey: ["/api/notifications"],
    refetchInterval: 60_000,
    staleTime: 0,
  });

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", `/api/notifications/${id}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const markAll = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/notifications/mark-all-read");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const unread = countQuery.data?.count ?? 0;
  const items = (listQuery.data?.items ?? []).slice(0, 8);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Notifications"
          data-testid="button-notification-bell"
          className="relative touch-target"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <Badge
              data-testid="badge-notification-unread-count"
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full text-[10px] flex items-center justify-center bg-red-600 text-white border-0"
            >
              {unread > 99 ? "99+" : unread}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[480px] overflow-auto">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={unread === 0 || markAll.isPending}
            onClick={() => markAll.mutate()}
            data-testid="button-mark-all-read-bell"
          >
            <Check className="h-3 w-3 mr-1" /> Mark all read
          </Button>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="text-empty-notifications">
            You're all caught up.
          </div>
        ) : (
          <div className="flex flex-col">
            {items.map((n) => (
              <NotificationRow
                key={n.id}
                n={n}
                onMarkRead={() => markRead.mutate(n.id)}
              />
            ))}
          </div>
        )}
        <DropdownMenuSeparator />
        <div className="flex items-center justify-between p-2">
          <Link href={inboxHref}>
            <a className="text-xs text-primary hover:underline" data-testid="link-open-inbox">
              View all
            </a>
          </Link>
          <Link href={preferencesHref}>
            <a className="text-xs text-muted-foreground hover:underline" data-testid="link-notification-preferences">
              Preferences
            </a>
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationRow({
  n,
  onMarkRead,
}: {
  n: Notification;
  onMarkRead: () => void;
}) {
  const created = n.createdAt ? new Date(n.createdAt) : null;
  const body = (
    <div
      className={`px-3 py-2 hover:bg-accent text-left ${n.readAt ? "opacity-70" : ""}`}
      data-testid={`row-notification-${n.id}`}
    >
      <div className="flex items-start gap-2">
        {!n.readAt && <span className="mt-1.5 inline-block h-2 w-2 rounded-full bg-primary shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate" data-testid={`text-notification-title-${n.id}`}>
            {n.title}
          </div>
          {n.body && <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>}
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {n.category.replace(/_/g, " ")}
            {created ? ` · ${created.toLocaleString()}` : ""}
          </div>
        </div>
      </div>
    </div>
  );
  return n.link ? (
    <Link href={n.link}>
      <a onClick={onMarkRead}>{body}</a>
    </Link>
  ) : (
    <button type="button" onClick={onMarkRead} className="block w-full">
      {body}
    </button>
  );
}
